import { Alert, Badge, Checkbox, Group, Loader, Stack, Text } from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import styles from './ReimbursableExpensesPicker.module.scss';
import { Props } from './ReimbursableExpensesPicker.types';

const formatAmount = (amount: number) => `${amount.toFixed(2)} €`;

const ReimbursableExpensesPicker = ({
  options,
  value,
  onChange,
  loading,
  error,
  notice,
}: Props) => {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className={styles.container}>
        <Text size="sm" fw={500}>
          {t('moneyTransfer.form.linkedExpenses', 'Dépenses remboursées')}
        </Text>
        <Group gap="xs" className={styles.loading}>
          <Loader size="xs" />
          <Text size="sm" c="dimmed">
            {t('moneyTransfer.form.linkedExpensesLoading', 'Recherche des dépenses à rembourser…')}
          </Text>
        </Group>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <Checkbox.Group
        value={value}
        onChange={onChange}
        label={t('moneyTransfer.form.linkedExpenses', 'Dépenses remboursées')}
        description={t(
          'moneyTransfer.form.linkedExpensesDescription',
          'Cochez les dépenses que ce remboursement solde. Le montant est calculé automatiquement.'
        )}
        error={error ?? undefined}
      >
        {options.length === 0 ? (
          <Text size="sm" c="dimmed" mt="xs">
            {t(
              'moneyTransfer.form.linkedExpensesEmpty',
              'Aucune dépense à rembourser pour ce bénéficiaire.'
            )}
          </Text>
        ) : (
          <Stack gap="xs" mt="xs">
            {options.map(option => (
              <Checkbox
                key={option.id}
                value={option.id}
                classNames={{ root: styles.item, labelWrapper: styles.labelWrapper }}
                label={
                  <div className={styles.line}>
                    <div className={styles.details}>
                      <Text size="sm" fw={500}>
                        {option.title}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {dayjs(option.date).format('DD/MM/YYYY')}
                      </Text>
                    </div>
                    <div className={styles.amounts}>
                      <Text size="sm" fw={500}>
                        {formatAmount(option.expectedAmount)}
                      </Text>
                      {option.isShareOnly && (
                        <Text size="xs" c="dimmed">
                          {t(
                            'moneyTransfer.form.linkedExpenseYourShare',
                            'votre part sur {{total}}',
                            {
                              total: formatAmount(option.totalAmount),
                            }
                          )}
                        </Text>
                      )}
                    </div>
                  </div>
                }
                description={
                  option.settledSharesCount ? (
                    <Badge color="blue" variant="light" size="sm">
                      {t('expenses.status.partiallyReimbursed', 'Remboursé {{settled}}/{{total}}', {
                        settled: option.settledSharesCount,
                        total: option.totalSharesCount ?? 0,
                      })}
                    </Badge>
                  ) : undefined
                }
              />
            ))}
          </Stack>
        )}
      </Checkbox.Group>

      {notice && (
        <Alert color="blue" variant="light" icon={<IconInfoCircle size={18} />} mt="xs">
          {notice}
        </Alert>
      )}
    </div>
  );
};

export default ReimbursableExpensesPicker;
